import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Col, Row } from 'react-bootstrap';
import moment from 'moment';
import LoadingSpinner from '~/elements/LoadingSpinner';

//components
import withLocalization from '../../../hoc/withLocalization';
import applicationRouter from '../../../hoc/applicationRouter';

//elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import Select from '../../../elements/Select';
import { CurrentUserGroupsWidget } from '../../../components/GenericForm/Widgets/UserGroupWidget';
import { NumberInput } from '../../../elements/Input';

const shiftTypes = [
    { value: 'dayShift', label: 'Day' },
    { value: 'afternoonShift', label: 'Afternoon' },
    { value: 'nightShift', label: 'Night' },
];

const getWeekDays = (weekStart, weekEnd) => {
    const days = [];
    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    let current = moment(weekStart);
    const end = moment(weekEnd);

    while (current.isSameOrBefore(end)) {
        const dayIndex = current.isoWeekday() - 1;
        days.push({
            key: dayNames[dayIndex],
            dayName: current.format('dddd'),
            date: current.format('DD.MM.YYYY')
        });
        current = current.add(1, 'days');
    }
    return days;
}

const formatOptions = (values) => values.map((ele) => ({ value: ele.id, label: ele.name }))

const getDayBgColor = (dayKey) => dayKey === 'saturday' || dayKey === 'sunday' ? 'weekly-shift-day-body-weekend' : 'weekly-shift-day-body-weekday';

const getPendingKey = (date, shiftType) => `${date}_${shiftType}`;

class ResourcePlannerForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            department: '',
            departmentOptions: [],
            skillsOptions: [],
            companyShiftsOptions: [],
            cycleDetails: {},
            cycleData: {},
            isLoading: false,
            workHoursRuleByDepartment: {},
            selectedWeek: {},
            pendingEntry: {},
        };
    }

    componentDidMount() {
        this.loadDepartment();
    }

    loadDepartment = async () => {
        const getDepartments = await this.props.rosterStore.getRuleBasedDepartments({ rule: 'work_hours_rules' });
        const department = this.props.router?.location.state.department;
        const _department = getDepartments?.availableDepartment.find((ele) => ele.value === department);
        this.setState(
            {
                departmentOptions: getDepartments?.availableDepartment,
                department: _department ? _department?.value : getDepartments?.availableDepartment[0]?.value,
            },
            () => {
                this.loadData();
            }
        );
    };

    loadData = async () => {
        const { clientStore, skillsStore, commonStore } = this.props;
        try {
            const { allow_union_approval, lockInPeriodForUnionApproval, lockInPeriodForPublish } = commonStore.config?.client?.data?.basicRules?.workPlanSettings || {};

            this.setState({ isLoading: true });
            const [workHoursRuleByDepartment, skills] = await Promise.all([
                clientStore.getWorkHoursRuleByDepartment(this.state.department),
                skillsStore.loadList({})
            ]);
            const skillsOptions = formatOptions(skills);

            const companyShifts = commonStore.config?.companyShifts || [];
            const companyShiftsOptions = companyShifts.map((cs) => ({ value: cs.id, label: cs.shift_name, shiftType: cs.shift_type }));

            const { cycle_week_length, rule_cycle_start_date } = workHoursRuleByDepartment;
            const cycleLenDays = cycle_week_length * 7;
            let startDate = moment(rule_cycle_start_date).startOf('day');
            
            let cutoff = moment()
                .startOf('day')
                .add(allow_union_approval ? lockInPeriodForUnionApproval : lockInPeriodForPublish, 'days');

            if (!cutoff.isBefore(startDate)) {
                const diffDays = cutoff.diff(startDate, 'days');
                const cyclesToAdvance = Math.floor(diffDays / cycleLenDays) + 1;
                startDate = moment(startDate).add(cyclesToAdvance * cycleLenDays, 'days');
            }

            let endDate = moment(startDate).add(cycle_week_length, 'weeks').subtract(1, 'day');
            startDate = startDate.format('YYYY-MM-DD');
            endDate = endDate.format('YYYY-MM-DD');

            this.setState({ workHoursRuleByDepartment, cycleDetails: { startDate, endDate }, skillsOptions, companyShiftsOptions }, () => {
                this.setState({ isLoading: false });
                this.initializeCycleData();
            });
        } catch (e) {
            console.error(e);
            this.props.router.navigate('/admin/roster');
        }
    };

    loadWeekDetails = async (weekStart, weekEnd) => {
        const { resourcePlanStore } = this.props;
        const { department } = this.state;
        const resource_plan = await resourcePlanStore.getPlan({ department, week_start_date: weekStart, week_end_date: weekEnd });
        return resource_plan
    }

    loadCycleData = (startDate, endDate, savedPlan) => {
        const initializedCycleData = {};
        const { plan_details = {} } = savedPlan || {};

        const dayCursor = moment(startDate).startOf('isoWeek');

        while (dayCursor.isSameOrBefore(endDate, 'day')) {
            if (dayCursor.isSameOrAfter(startDate, 'day')) {
                const dateKey = dayCursor.format('YYYY-MM-DD');

                initializedCycleData[dateKey] = Object.fromEntries(
                    shiftTypes.map(({ value }) => [
                        value,
                        plan_details?.[dateKey]?.[value]
                            ? { ...plan_details[dateKey][value] }
                            : {},
                    ])
                );
            }

            dayCursor.add(1, 'day');
        }

        return initializedCycleData;
    }

    initializeCycleData = async () => {
        const { cycleDetails } = this.state;
        
        const cycleStart = moment(cycleDetails.startDate);
        const weekStart = cycleStart.clone().startOf('isoWeek').format('YYYY-MM-DD');
        const weekEnd = cycleStart.clone().endOf('isoWeek').format('YYYY-MM-DD');

        const savedWeekPlan = await this.loadWeekDetails(weekStart, weekEnd);
        const initializedCycleData = this.loadCycleData(weekStart, weekEnd, savedWeekPlan);

        this.setState({
            cycleData: {...this.state.cycleData, [cycleStart.isoWeek()]: initializedCycleData},
            selectedWeek: {
                week: cycleStart.isoWeek(),
                start: weekStart,
                end: weekEnd,
                days: getWeekDays(weekStart, weekEnd),
            },
        });
    };

    handleChange = (field, value) => {
        this.setState({ [field]: value, isLoading: true }, () => this.loadData());
    };

    handlePrevWeek = () => {
        const { selectedWeek, cycleDetails } = this.state;
        if (!selectedWeek) return;
        if (moment(cycleDetails.startDate).isSame(selectedWeek.start)) return;
        this.updateWeekData(moment(selectedWeek.start).subtract(1, 'weeks'));
    };

    handleNextWeek = () => {
        const { selectedWeek, cycleDetails } = this.state;
        if (!selectedWeek) return;
        if (moment(cycleDetails.endDate).isSame(selectedWeek.end)) return;
        this.updateWeekData(moment(selectedWeek.start).add(1, 'weeks'));
    };

    updateWeekData = async (targetDate) => {
        const { cycleDetails, cycleData } = this.state;
        const weekNumber = moment(targetDate).isoWeek();
        let weekStart = moment(targetDate).clone().startOf('isoWeek').format('YYYY-MM-DD');
        let weekEnd = moment(targetDate).clone().endOf('isoWeek').format('YYYY-MM-DD');

        const cycleStart = moment(cycleDetails.startDate);
        const cycleEnd = moment(cycleDetails.endDate);

        if (!moment(weekStart).isBetween(cycleStart, cycleEnd, 'date', '[]')) {
            weekStart = cycleStart.format('YYYY-MM-DD');
        }
        if (!moment(weekEnd).isBetween(cycleStart, cycleEnd, 'date', '[]')) {
            weekEnd = cycleEnd.format('YYYY-MM-DD');
        }

        let weekCycleData = cycleData[weekNumber];
        if (!weekCycleData) {
            const savedWeekPlan = await this.loadWeekDetails(weekStart, weekEnd);
            weekCycleData = this.loadCycleData(weekStart, weekEnd, savedWeekPlan);
        }

        this.setState({
            cycleData: { ...cycleData, [weekNumber]: weekCycleData },
            selectedWeek: {
                week: weekNumber,
                start: weekStart,
                end: weekEnd,
                days: getWeekDays(weekStart, weekEnd),
            },
        });
    };

    getPending = (date, shiftType) => {
        const key = getPendingKey(date, shiftType);
        return this.state.pendingEntry[key] || { companyShift: null, headCount: '', group: null, skill: null, count: 0 };
    };

    handlePendingChange = (date, shiftType, field, value) => {
        const key = getPendingKey(date, shiftType);
        const update = { [field]: value };

        if (field === 'companyShift') {
            const shiftId = value ? String(value.value) : null;
            const _date = moment(date, 'DD.MM.YYYY').format('YYYY-MM-DD');
            const { selectedWeek, cycleData } = this.state;
            const existingEntry = shiftId ? cycleData[selectedWeek.week]?.[_date]?.[shiftType]?.[shiftId] : null;
            update.headCount = existingEntry?.headCount || '';
            update.group = null;
            update.skill = null;
            update.count = 0;
        }

        this.setState((prev) => ({
            pendingEntry: {
                ...prev.pendingEntry,
                [key]: { ...this.getPending(date, shiftType), ...update },
            },
        }));
    };

    handleAdd = (date, shiftType) => {
        const pending = this.getPending(date, shiftType);
        const { companyShift, headCount, group, skill, count } = pending;

        if (!companyShift || !(headCount >= 1)) return;

        const shiftId = String(companyShift.value);
        const _date = moment(date, 'DD.MM.YYYY').format('YYYY-MM-DD');
        const { selectedWeek, cycleData } = this.state;
        const weekData = cycleData[selectedWeek.week];
        const shiftTypeData = weekData[_date][shiftType];

        if (!shiftTypeData[shiftId]) {
            shiftTypeData[shiftId] = { headCount: null, categoryWiseCount: {} };
        }

        shiftTypeData[shiftId].headCount = Number(headCount);

        if ((group || skill) && count >= 1) {
            const newEntries = { ...shiftTypeData[shiftId].categoryWiseCount };

            if (group) {
                const groupKey = `group_${group}`;
                newEntries[groupKey] = {
                    label: group,
                    value: group,
                    type: 'group',
                    count: (newEntries[groupKey]?.count || 0) + Number(count),
                };
            }

            if (skill) {
                const skillKey = `skill_${skill.value}`;
                newEntries[skillKey] = {
                    label: skill.label,
                    value: skill.value,
                    type: 'skill',
                    count: (newEntries[skillKey]?.count || 0) + Number(count),
                };
            }

            const totalAssignedCount = Object.values(newEntries).reduce((p, c) => p + c.count, 0);
            shiftTypeData[shiftId].categoryWiseCount = newEntries;
            if (shiftTypeData[shiftId].headCount < totalAssignedCount) {
                shiftTypeData[shiftId].headCount = totalAssignedCount;
            }
        }

        this.setState({
            cycleData: { ...cycleData, [selectedWeek.week]: weekData },
            pendingEntry: {
                ...this.state.pendingEntry,
                [getPendingKey(date, shiftType)]: { companyShift, headCount: Number(headCount), group: null, skill: null, count: 0 },
            },
        });
    };

    handleRemoveShift = (date, shiftType, shiftId) => {
        const _date = moment(date, 'DD.MM.YYYY').format('YYYY-MM-DD');
        const { selectedWeek, cycleData } = this.state;
        const weekData = cycleData[selectedWeek.week];
        const updated = { ...weekData[_date][shiftType] };
        delete updated[shiftId];
        weekData[_date][shiftType] = updated;
        this.setState({ cycleData: { ...cycleData, [selectedWeek.week]: weekData } });
    };

    handleRemoveCategory = (date, shiftType, shiftId, categoryKey) => {
        const _date = moment(date, 'DD.MM.YYYY').format('YYYY-MM-DD');
        const { selectedWeek, cycleData } = this.state;
        const weekData = cycleData[selectedWeek.week];

        const updated = { ...weekData[_date][shiftType][shiftId].categoryWiseCount };
        delete updated[categoryKey];
        weekData[_date][shiftType][shiftId].categoryWiseCount = updated;

        this.setState({ cycleData: { ...cycleData, [selectedWeek.week]: weekData } });
    };

    handleApplyToAllShifts = (date, shiftType) => {
        const _date = moment(date, 'DD.MM.YYYY').format('YYYY-MM-DD');
        const { selectedWeek, cycleData } = this.state;
        const weekData = { ...cycleData[selectedWeek.week] };
        const source = weekData[_date][shiftType];

        weekData[_date] = { ...weekData[_date] };
        shiftTypes.forEach((s) => {
            if (s.value !== shiftType) {
                weekData[_date][s.value] = JSON.parse(JSON.stringify(source));
            }
        });

        this.setState({ cycleData: { ...cycleData, [selectedWeek.week]: weekData } });
    };

    handleSave = async () => {
        const { commonStore, resourcePlanStore } = this.props;
        const { cycleData, cycleDetails } = this.state;
        this.setState({ loading: true });

        const data = {};
        Object.entries(cycleData).forEach(([weekNumber, weekData]) => {
            data[weekNumber] = {};
            Object.entries(weekData).forEach(([dateKey, dayData]) => {
                data[weekNumber][dateKey] = {};
                Object.entries(dayData).forEach(([shiftKey, shiftData]) => {
                    const shiftEntries = {};
                    Object.entries(shiftData).forEach(([shiftId, entry]) => {
                        if (entry.headCount >= 1) {
                            shiftEntries[shiftId] = {
                                headCount: entry.headCount,
                                categoryWiseCount: entry.categoryWiseCount,
                            };
                        }
                    });
                    if (Object.keys(shiftEntries).length > 0) {
                        data[weekNumber][dateKey][shiftKey] = shiftEntries;
                    }
                });
            });
        });

        const payload = {
            department: this.state.department,
            cycle_start_date: moment(cycleDetails?.startDate).format('YYYY-MM-DD'),
            cycle_end_date: moment(cycleDetails?.endDate).format('YYYY-MM-DD'),
            plan_details: data,
        };

        try {
            const savedTemplate = await resourcePlanStore.save(payload);
            if (savedTemplate) {
                commonStore.addNotification(savedTemplate.message, null, 'success');
                this.props.router.navigate('/admin/roster');
            }
        } catch (err) {
            console.error(err);
        } finally {
            this.setState({ loading: false });
        }
    };

    render() {
        const { commonStore, t, userGroupStore } = this.props;
        const { isLoading, selectedWeek, cycleData, cycleDetails, department, departmentOptions, skillsOptions, companyShiftsOptions } = this.state;

        if (!selectedWeek) return null;
        if (isLoading) return <LoadingSpinner />;

        const weekDays = selectedWeek?.days || [];
        const weekNumber = selectedWeek?.week;
        const weekData = weekNumber ? cycleData[weekNumber] || {} : {};

        const dateTimeRules = commonStore.getDateTimeRules();

        const skillMap = new Map();
        skillsOptions?.forEach((ele) => { skillMap.set(ele.value, ele.label); });

        return (
            <>
                <Row>
                    <Col xs={2}>
                        <label className="control-label mb-1 fs15">{t('Department')}</label>
                        <Select
                            placeholder={t('Select...')}
                            options={departmentOptions}
                            value={departmentOptions.find((ele) => ele.value === department)}
                            onChange={(e) => this.handleChange('department', e?.value)}
                            disabled
                        />
                    </Col>
                    <Col xs={7} className="d-flex align-items-end">
                        <div className="d-flex justify-content-center gap-2">
                            <Button
                                fill dynamic_lg
                                onClick={this.handlePrevWeek}
                                disabled={moment(cycleDetails.startDate).isSame(selectedWeek.start)}
                            >
                                <i className="fas fa-chevron-left"></i>
                                <span className="ms-2">{t('Prev')}</span>
                            </Button>
                            <div style={{ background: '#bcc2c4', borderRadius: 8 }}>
                                <span
                                    className="fw-bold d-flex align-items-center m-2 mt-2 fs15"
                                    style={{ background: 'white', border: '4px solid #fff', borderRadius: 5 }}
                                >
                                    {t('Week')} {selectedWeek.week} : {moment(selectedWeek.start).format(dateTimeRules.short_date_format)} -{' '}
                                    {moment(selectedWeek.end).format(dateTimeRules.short_date_format)}
                                </span>
                            </div>
                            <Button
                                fill dynamic_lg
                                onClick={this.handleNextWeek}
                                disabled={moment(cycleDetails.endDate).isSame(selectedWeek.end)}
                            >
                                <span className="me-2">{t('Next')}</span>
                                <i className="fas fa-chevron-right"></i>
                            </Button>
                        </div>
                    </Col>
                    <Col xs={3} className="d-flex justify-content-end align-items-end gap-2">
                        <Button variant="outline-danger" onClick={() => this.props.router.navigate('/admin/roster')}>
                            {t('Cancel')}
                        </Button>
                        <Button fill dynamic_lg onClick={() => this.handleSave()}>
                            {t('Save')}
                        </Button>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12} className="mt-3 pe-0">
                        <div className="modal-container" style={{ overflow: 'auto', height: '75vh' }}>
                            <div className="row me-0 flex-column">
                                <div className="col-12 mb-2">
                                    <Row className="weekly-shift-day-card ms-0 weekly-shift-day-header-weekday text-light">
                                        <Col sm={1} className="p-2">
                                            <span className="fw-bold fs15">{t('Day')}</span>
                                        </Col>
                                        <Col sm={1} className="p-2">
                                            <span className="fw-bold fs15">{t('Shift')}</span>
                                        </Col>
                                        <Col sm={2} className="p-2">
                                            <span className="fw-bold fs15">{t('Company Shift')}</span>
                                        </Col>
                                        <Col sm={1} className="p-2">
                                            <span className="fw-bold fs15">{t('No. of members')}</span>
                                        </Col>
                                        <Col sm={2} className="p-2">
                                            <span className="fw-bold fs15">
                                                {t('Group')} | {t('Skills')}
                                            </span>
                                        </Col>
                                        <Col sm={1} className="p-2">
                                            <span className="fw-bold fs15">{t('Count')}</span>
                                        </Col>
                                        <Col sm={2} className="p-2">
                                            <span className="fw-bold fs15">{t('Action')}</span>
                                        </Col>
                                        <Col sm={2} className="p-2">
                                            <span className="fw-bold fs15">{t('Skill / Group wise count')}</span>
                                        </Col>
                                    </Row>
                                </div>

                                {weekDays.map((day) => {
                                    const _date = moment(day.date, 'DD.MM.YYYY').format('YYYY-MM-DD');
                                    return (
                                        <div key={day.key}>
                                            {shiftTypes.map((shift) => {
                                                const dayShiftData = (weekData[_date] && weekData[_date][shift.value]) || {};
                                                const pending = this.getPending(day.date, shift.value);
                                                const hasCompanyShift = !!pending.companyShift;
                                                const hasAnyCategorySelected = !!(pending.group || pending.skill);

                                                const selectedShiftId = pending.companyShift ? String(pending.companyShift.value) : null;
                                                const currentShiftEntry = selectedShiftId ? dayShiftData[selectedShiftId] : null;

                                                return (
                                                    <div key={`${day.key}_${shift.label}`} className="col-12 mb-2">
                                                        <Row className={`weekly-shift-day-card ms-0 ${getDayBgColor(day.key)}`}>
                                                            <Col sm={1} className="p-2">
                                                                <span className="fw-bold fs15">{t(day?.dayName)}</span>
                                                            </Col>
                                                            <Col sm={1} className="p-2">
                                                                <span className="fw-bold fs15">{t(shift?.label)}</span>
                                                            </Col>
                                                            <Col sm={2} className="p-2">
                                                                <Select
                                                                    placeholder={t('Select Company Shift')}
                                                                    value={pending.companyShift}
                                                                    options={companyShiftsOptions.filter(o => shift?.value === o.shiftType)}
                                                                    onChange={(s) => this.handlePendingChange(day.date, shift.value, 'companyShift', s)}
                                                                    isClearable
                                                                />
                                                            </Col>
                                                            <Col sm={1} className="p-2">
                                                                <NumberInput
                                                                    style={{ maxWidth: '70px' }}
                                                                    value={
                                                                        hasCompanyShift
                                                                            ? (pending.headCount === '' ? '' : pending.headCount)
                                                                            : (Object.values(dayShiftData).reduce((s, e) => s + (e.headCount || 0), 0) || '')
                                                                    }
                                                                    disabled={!hasCompanyShift}
                                                                    onChange={(res) => this.handlePendingChange(day.date, shift.value, 'headCount', parseInt(res.target.value) || '')}
                                                                />
                                                            </Col>
                                                            <Col sm={2} className="p-2">
                                                                <div className="row flex-row">
                                                                    <CurrentUserGroupsWidget
                                                                        value={pending.group}
                                                                        onChange={(value) => this.handlePendingChange(day.date, shift.value, 'group', value)}
                                                                    />
                                                                    <Select
                                                                        className="mt-1"
                                                                        placeholder={t('Select Skill')}
                                                                        value={pending.skill}
                                                                        options={skillsOptions}
                                                                        onChange={(s) => this.handlePendingChange(day.date, shift.value, 'skill', s)}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col sm={1} className="p-2">
                                                                <NumberInput
                                                                    style={{ maxWidth: '70px' }}
                                                                    value={pending.count}
                                                                    disabled={!hasAnyCategorySelected || !hasCompanyShift}
                                                                    onChange={(res) =>
                                                                        this.handlePendingChange(day.date, shift.value, 'count', Number(res.target.value))
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col sm={2} className="p-2 row flex-row">
                                                                <Button
                                                                    md
                                                                    fill
                                                                    disabled={!hasCompanyShift || !(pending.headCount >= 1)}
                                                                    onClick={() => this.handleAdd(day.date, shift.value)}
                                                                >
                                                                    {t('Add')}
                                                                </Button>
                                                                <Button
                                                                    md
                                                                    className="mt-1"
                                                                    disabled={!hasCompanyShift || !(currentShiftEntry?.headCount >= 1)}
                                                                    onClick={() => this.handleApplyToAllShifts(day.date, shift.value)}
                                                                >
                                                                    {t('Apply to all shifts')}
                                                                </Button>
                                                            </Col>
                                                            <Col sm={2} className="p-2">
                                                                {Object.keys(dayShiftData).length === 0 && (
                                                                    <span className="text-muted fs-6 fst-italic">{t('None')}</span>
                                                                )}
                                                                {Object.entries(dayShiftData).map(([shiftId, shiftEntry]) => {
                                                                    if (!(shiftEntry?.headCount >= 1)) return null;
                                                                    const shiftLabel = companyShiftsOptions.find((o) => String(o.value) === shiftId)?.label || shiftId;
                                                                    const categories = shiftEntry?.categoryWiseCount || {};
                                                                    return (
                                                                        <div key={shiftId} className="mb-1">
                                                                            <small className="fw-bold text-muted d-flex align-items-center gap-1">
                                                                                <span>{shiftLabel} ({shiftEntry.headCount || 0})</span>
                                                                                <span
                                                                                    className="cursor-pointer text-danger"
                                                                                    style={{ fontWeight: 'bold', fontSize: 13 }}
                                                                                    onClick={() => this.handleRemoveShift(day.date, shift.value, shiftId)}
                                                                                    title={t('Remove')}
                                                                                >
                                                                                    ×
                                                                                </span>
                                                                            </small>
                                                                            <div className="d-flex flex-wrap gap-1">
                                                                                {Object.keys(categories).length === 0 && (
                                                                                    <span className="text-muted fst-italic" style={{ fontSize: 12 }}>{t('No breakdown')}</span>
                                                                                )}
                                                                                {Object.entries(categories).map(([key, entry]) => (
                                                                                    <span
                                                                                        key={key}
                                                                                        className="badge d-inline-flex align-items-center gap-1"
                                                                                        style={{
                                                                                            backgroundColor: entry.type === 'group' ? '#4a90d9' : '#6abf69',
                                                                                            color: '#fff',
                                                                                            borderRadius: 12,
                                                                                            fontSize: 12,
                                                                                            padding: '3px 8px',
                                                                                        }}
                                                                                    >
                                                                                        <span>
                                                                                            {entry.type === 'group' ? '👥 ' : '🔧 '}
                                                                                            {entry.type === 'group' ? userGroupStore.getUserGroup(entry.value)?.name : skillMap.get(entry.value)} ({entry.count})
                                                                                        </span>
                                                                                        <span
                                                                                            className="ms-1 cursor-pointer"
                                                                                            style={{ fontWeight: 'bold', fontSize: 13 }}
                                                                                            onClick={() => this.handleRemoveCategory(day.date, shift.value, shiftId, key)}
                                                                                            title={t('Remove')}
                                                                                        >
                                                                                            ×
                                                                                        </span>
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </Col>
                </Row>
            </>
        );
    }
}

export default inject('userStore', 'commonStore', 'clientStore', 'rosterStore', 'skillsStore', 'resourcePlanStore', 'userGroupStore')(withLocalization(applicationRouter(observer(ResourcePlannerForm))));
